// DOLE help content for the PESO dashboard. Every `url` is an official DOLE government
// page, verified live (2026-07-23). Explanations are plain-language ("assume they know
// nothing"). Single source of truth so the same content can extend to other tabs later.
//
// Shape: { title, body, source, url, linkLabel? }

const PES = 'https://dole.gov.ph/public-employment-services-pes-contents/';
const RUBRIC = 'https://dole9.org/external/ea3ef22c4a7ffe7211a22c711d791e49.pdf';

export const DOLE_SOURCES = {
  award: {
    title: 'Your Best PESO score',
    body: 'Every year DOLE scores each PESO out of 100 points on how well it delivers employment services. Your office is compared with other towns of the same size (Panglao is a 1st-class municipality). The best office in each region is nominated and can win at the National PESO Congress — finalists generally reach at least 75 points. This scoreboard adds up your points as you earn them through the year.',
    source: 'DOLE Best-PESO Points System Reference',
    url: RUBRIC, linkLabel: 'Open DOLE’s scoring guide (PDF)',
  },
  placement: {
    title: 'Referral & Placement — 30 points',
    body: 'Your placement rate = people you PLACED (got hired) ÷ people you REFERRED to employers, ×100. Of the jobseekers you sent to jobs, how many were actually hired? DOLE gives the full 30 points at 75% or higher.',
    source: 'Referral & placement is a core PESO service (RA 8759).', url: PES,
  },
  programs: {
    title: 'DOLE Programs implemented — 25 points',
    body: 'Points for how many DOLE employment and livelihood programs your office actually runs (SPES, TUPAD, JobStart, GIP, livelihood, and more). Running more than 10 earns the full 25 points. Tick each program you implement — every one has its own DOLE guide.',
    source: 'PESO delivers DOLE programs and projects.', url: PES,
  },
  lmi: {
    title: 'LMI Reports — 15 points',
    body: 'LMI (Labor Market Information) reports are the monthly reports you submit to DOLE. Filing all 12 on time in a year earns the full 15 points. A LATE report does not count — so filing on time is what matters.',
    source: 'Labor market information is a core PESO service.', url: PES,
  },
  career: {
    title: 'Career Guidance & Counseling — 10 points',
    body: 'DOLE counts people reached by career guidance AND employment counseling. Two parts add up here. (1) Job applicants you counseled — this fills in automatically from the “Counseling” boxes you tick on Form R1 (Applicants Registered), so tick them as you serve people. (2) Students and parents you reach in career-guidance talks and school sessions — the platform can’t see those, so you type that number each month. For a 1st-class municipality, 1,000+ a year earns the full 10 points.',
    source: 'Career guidance & employment counseling are core PESO services.', url: PES,
  },
  awards: {
    title: 'Awards — 10 points',
    body: 'Points for employment-related awards your office received from respected institutions. DOLE does not publish an exact formula here — a panel decides — so enter your own honest 0–10 estimate and keep your certificates on file.',
    source: 'DOLE Best-PESO Points System Reference',
    url: RUBRIC, linkLabel: 'Open DOLE’s scoring guide (PDF)',
  },
  leadership: {
    title: 'Leadership in organizations — 10 points',
    body: 'Points for your office’s active membership and leadership in organizations that help employment — the PESO Managers’ Association, local development councils, chambers of commerce. Being active in 8 or more earns the full 10 points.',
    source: 'DOLE Best-PESO Points System Reference',
    url: RUBRIC, linkLabel: 'Open DOLE’s scoring guide (PDF)',
  },
  entry: {
    title: 'Entering your monthly numbers',
    body: 'Bohol Trabaho counts what happens on the platform automatically (the “Platform counted” figure). But your real DOLE numbers include work done off-platform — walk-ins served at your counter, or vacancies an employer posted on their own Facebook page. Type those extra counts here and they are added to your total. DOLE counts ALL vacancies you solicited, whatever the channel. Pick any month — including months before you used this system — and enter the numbers by hand.',
    source: 'Employment facilitation counts all channels (RA 8759).', url: PES,
  },
  sprs: {
    title: 'Your annual SPRS targets',
    body: 'For registrations, referrals, vacancies solicited and job fairs, DOLE does not set a fixed number every office must hit. Instead, each PESO commits its own yearly target in its SPRS work plan, and DOLE grades your performance as a percentage of that target (for example “124% of target”). The monthly goals on the cards are simply your annual target ÷ 12. Career guidance, placement rate and LMI reports are different — DOLE fixes those numbers.',
    source: 'PESO performance is measured against SPRS targets.', url: PES,
  },
  kpi_registered: {
    title: 'Job seekers registered',
    body: 'Everyone who signs up with your office — through your NSRP link or at the counter — counts as “registered.” This is the base of all the people you serve.',
    source: 'PESO registration & the skills registry (NSRP / PhilJobNet).',
    url: 'https://ble.dole.gov.ph/about-philjobnet/',
  },
  kpi_referred: {
    title: 'Referred',
    body: 'A referral is when you send a registered jobseeker to an employer’s vacancy. On this platform, a jobseeker’s job application is the referral. “Referred” is the bottom of your placement rate.',
    source: 'Referral & placement is a core PESO service.', url: PES,
  },
  kpi_placed: {
    title: 'Placed',
    body: 'A placement is a jobseeker who actually got hired after you referred them. Placed ÷ Referred is your placement rate — the biggest single item (30 pts) in the Best-PESO score.',
    source: 'Referral & placement is a core PESO service.', url: PES,
  },
  kpi_vacancies: {
    title: 'Vacancies solicited',
    body: 'Job openings you gathered from employers to fill. The more vacancies you solicit, the more chances you have to place jobseekers.',
    source: 'Vacancy solicitation is a core PESO service.', url: PES,
  },
  kpi_lmi: {
    title: 'LMI report packet',
    body: 'Your monthly DOLE report. Filing it on time each month builds toward the 15-point LMI line. Late filings are not counted.',
    source: 'Labor market information reporting.', url: PES,
  },
  prog_SPES: {
    title: 'SPES — Special Program for Employment of Students',
    body: 'Gives poor but deserving students and out-of-school youth (15–30) short-term jobs during school breaks, with part of their pay from DOLE. Your PESO takes applications and matches them to employers.',
    source: 'RA 7323, as amended.',
    url: 'https://dole.gov.ph/special-program-of-the-employment-of-students-spes-contents/',
  },
  prog_TUPAD: {
    title: 'TUPAD — emergency employment',
    body: 'Short-term emergency work (10–30 days) for displaced, underemployed or seasonal workers — clean-up drives, facility repair — with DOLE-paid wages. Your PESO identifies and processes beneficiaries.',
    source: 'DOLE — About TUPAD.', url: 'https://dole.gov.ph/tupad-contents/',
  },
  prog_JobStart: {
    title: 'JobStart Philippines',
    body: 'A full-cycle youth program (RA 10869): life-skills training + technical training + a paid internship, for at-risk young jobseekers. Your PESO registers trainees and links them to partner employers.',
    source: 'DOLE — JobStart Philippines Program.', url: 'https://dole.gov.ph/jobstart-philippines-program/',
  },
  prog_GIP: {
    title: 'GIP — Government Internship Program',
    body: 'Paid 3–6 month internships in government for youth (18–30) with no prior government work, at minimum wage. Your PESO endorses and helps place interns.',
    source: 'DOLE — About the Government Internship Program.',
    url: 'https://dole.gov.ph/government-internship-program-gip-contents/',
  },
  prog_DILEEP: {
    title: 'DILEEP / Kabuhayan — livelihood & emergency employment',
    body: 'DOLE’s umbrella for livelihood and emergency employment. The Kabuhayan livelihood grant gives marginalized workers starter capital (up to ₱20,000) for small businesses. Your PESO helps identify and endorse beneficiaries.',
    source: 'DOLE Bureau of Workers with Special Concerns — DILP/Kabuhayan.',
    url: 'https://bwsc.dole.gov.ph/dileep/dilp/',
  },
  prog_PhilJobNet: {
    title: 'PhilJobNet & the skills registry (NSRP)',
    body: 'PhilJobNet is DOLE’s free online job-matching website, backed by the National Skills Registration Program. Enrolling your jobseekers and employers here is countable PESO program activity.',
    source: 'DOLE Bureau of Local Employment — About PhilJobNet.',
    url: 'https://ble.dole.gov.ph/about-philjobnet/',
  },
  prog_jobfair: {
    title: 'Job fairs',
    body: 'A job fair is a DOLE-recognized event where many employers and jobseekers meet on one day for on-the-spot hiring. Hosting or co-hosting them is countable employment-facilitation activity.',
    source: 'Employment facilitation is a core PESO service.', url: PES,
  },
  prog_tesda: {
    title: 'TESDA skills-training referral',
    body: 'Referring jobseekers to free TESDA skills training (and scholarships) so they qualify for in-demand jobs. Referral to training partners is part of your PESO’s employment-facilitation role.',
    source: 'Referral & information is a core PESO service.', url: PES,
  },
};

// Program checklist label -> help key. Labels match state.programs in the mockup.
export const PROGRAM_HELP = {
  'SPES': 'prog_SPES',
  'SPES 2nd tranche': 'prog_SPES',
  'TUPAD': 'prog_TUPAD',
  'JobStart': 'prog_JobStart',
  'GIP': 'prog_GIP',
  'DILEEP': 'prog_DILEEP',
  'Livelihood (DILP)': 'prog_DILEEP',
  'PhilJobNet drive': 'prog_PhilJobNet',
  'Job fair hosting': 'prog_jobfair',
  'TESDA referral': 'prog_tesda',
};

// The canonical program list an office can tick (order shown in the editor).
export const PROGRAM_OPTIONS = [
  'SPES', 'TUPAD', 'JobStart', 'GIP', 'DILEEP', 'Livelihood (DILP)',
  'PhilJobNet drive', 'Job fair hosting', 'TESDA referral', 'SPES 2nd tranche',
];
